/**
 * Bottom-of-page lexicon — every badge, chip and glyph the cockpit uses, rendered with the
 * REAL classes so the legend looks exactly like the page it explains.
 *
 * Shown on EVERY view (fleet and story alike). The hover tooltips carry the same
 * definitions in place; the legend is the one place to read them all at once.
 *
 * Deliberately covers only what this build actually renders. The benefit sector tags are
 * absent because nothing shows them yet — a legend that describes things not on screen is
 * worse than no legend, because it makes the reader hunt for them.
 */
import React from 'react';
import { StageDot } from './StageDot';

const Row = ({ badge, children }: { badge: React.ReactNode; children: React.ReactNode }) => (
  <div className="pmc-lrow">{badge}<span className="pmc-ldesc">{children}</span></div>
);

const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="pmc-lgroup">
    <div className="pmc-lgroup-title">{title}</div>
    {children}
  </div>
);

export function Legend() {
  return (
    <details className="pmc-legend">
      <summary>Legend — every badge &amp; chip used in the cockpit</summary>
      <div className="pmc-lgrid">

        <Group title="The value chain (table columns · story steps)">
          <Row badge={<span className="pmc-ldesc pmc-lstrong">INGEST → PERFORM</span>}>
            feed arrival → AI-learned attributes → category dictionary → benefit matching →
            AI-written page → live URLs and languages → search performance
          </Row>
          <Row badge={<span className="pmc-ldesc pmc-lstrong">search (GSC)</span>}>
            Google Search Console impressions and average position recorded for the live page
          </Row>
        </Group>

        <Group title="Step dots &amp; the colour contract">
          <Row badge={<StageDot stage="ingest" state="good" />}>good — the stage predicate passes</Row>
          <Row badge={<StageDot stage="ingest" state="partial" />}>partial / weak — something is there, but incomplete</Row>
          <Row badge={<StageDot stage="ingest" state="poor" />}>poor — the stage predicate fails</Row>
          <Row badge={<StageDot stage="ingest" state="unknown" />}>
            hollow — unknown, or absent by design. Never read it as zero.
          </Row>
          <Row badge={<span className="pmc-syn pmc-lstrong">◆ orange</span>}>
            computed or phrased by the cockpit — everything else is shown verbatim from the database
          </Row>
        </Group>

        <Group title="Where a value comes from (recorded origin)">
          <Row badge={<span className="pmc-chipv pmc-o-feed">feed</span>}>verbatim from the merchant product feed</Row>
          <Row badge={<span className="pmc-chipv pmc-o-feed pmc-computed">feed</span>}>
            dashed = no origin was recorded, but the value EXACTLY equals a feed field — feed
            provenance computed by the cockpit, never mistake it for a recorded one
          </Row>
          <Row badge={<span className="pmc-chipv pmc-o-ai">ai</span>}>learned by AI enrichment (origin recorded in the knowledge base)</Row>
          <Row badge={<span className="pmc-chipv pmc-o-vendor">vendor</span>}>extracted from the vendor / manufacturer page</Row>
          <Row badge={<span className="pmc-chipv pmc-o-inferred">inferred ⚠</span>}>inferred by the AI, not extracted from a source</Row>
          <Row badge={<span className="pmc-chipv pmc-o-missing">unrecorded</span>}>the value exists but its origin was never recorded</Row>
        </Group>

        <Group title="Enrich ledger (merchant left · learned right)">
          <Row badge={<span className="pmc-lg-glyph pmc-lg-eq pmc-lflat">=</span>}>
            the learned value provably EQUALS the merchant field on the same line
          </Row>
          <Row badge={<span className="pmc-lg-glyph pmc-lg-norm pmc-lflat">~</span>}>
            same field, different spelling — normalised or parsed from the merchant value
          </Row>
          <Row badge={<span className="pmc-lg-glyph pmc-lg-new pmc-lflat">+</span>}>
            net new — nowhere in the merchant data: the enrichment value-add
          </Row>
        </Group>

        <Group title="Category dictionary (what the semantic layer knows)">
          <Row badge={<span className="pmc-sbadge pmc-key">★</span>}>key attribute for this category</Row>
          <Row badge={<span className="pmc-sbadge">def</span>}>a definition exists — open the row to read it</Row>
          <Row badge={<span className="pmc-sbadge">values</span>}>the allowed / known values are listed</Row>
          <Row badge={<span className="pmc-sbadge">hints</span>}>extraction hints exist from category research</Row>
          <Row badge={<span className="pmc-sbadge">range</span>}>a typical min–max range is known</Row>
        </Group>

        <Group title="Value check — is the extracted value one the dictionary knows?">
          <Row badge={<span className="pmc-sbadge pmc-vcheck pmc-vc-ok">✓</span>}>
            the value IS one of the known values
          </Row>
          <Row badge={<span className="pmc-sbadge pmc-vcheck pmc-vc-variant">≈</span>}>
            equals a known value up to case or format — a spelling variant to normalise
          </Row>
          <Row badge={<span className="pmc-sbadge pmc-vcheck pmc-vc-unvalidated">?</span>}>
            NOT among the known values: a wrong value, a true synonym, or a value the
            dictionary must learn — the queued semantic gap decides which
          </Row>
        </Group>

        <Group title="Generated sections">
          <Row badge={<span className="pmc-secchip">faq</span>}>
            one generated section — click the chip to read it rendered like the live page
          </Row>
          <Row badge={<span className="pmc-sbadge">data</span>}>
            deterministic section — built from stored data, not free generation
          </Row>
          <Row badge={<span className="pmc-sbadge pmc-gen">→ gen</span>}>
            the attribute was in the actual generation prompt (recorded from S3)
          </Row>
          <Row badge={<span className="pmc-sbadge pmc-gen pmc-off">✗ gen</span>}>
            in the knowledge base today but NOT in the prompt — added after generation, or never injected
          </Row>
        </Group>

        <Group title="The LLM judge — model OPINION, never a measurement">
          <Row badge={<span className="pmc-sbadge pmc-judge">LLM judge</span>}>
            a guided review of the generation CONTEXT against an editable per-section recipe.
            It may only report elements listed in that recipe, every finding must cite its
            evidence, and it never judges the writing itself.
          </Row>
          <Row badge={<span className="pmc-jverdict pmc-jv-major_gaps">MAJOR</span>}>
            the section's context is missing an element central to its family
          </Row>
          <Row badge={<span className="pmc-jtag pmc-major">missing</span>}>
            no trace of the recipe element in the recorded prompt or the category knowledge
          </Row>
          <Row badge={<span className="pmc-jtag pmc-weak-tag">weak</span>}>
            the element is present but visibly poor
          </Row>
          <Row badge={<span className="pmc-syn pmc-lstrong">⚠ N discarded</span>}>
            findings the model made OUTSIDE its recipe, or with no evidence. Validation drops
            them and counts them — a filter you cannot see is a filter you cannot trust.
          </Row>
          <Row badge={<span className="pmc-lstrong" style={{ color: 'var(--pmc-judge)' }}>violet</span>}>
            model opinion: neither verbatim database content nor a deterministic computation
          </Row>
        </Group>

        <Group title="Unsourced numbers (deterministic, not the judge)">
          <Row badge={<span className="pmc-secchip-dot pmc-warn" />}>
            a number in the recorded response that its own prompt never contained — a
            hallucination CANDIDATE, shown with its excerpt because it is not proof
          </Row>
        </Group>

      </div>
    </details>
  );
}
